import "./TasksDashboardHeader.css";

import { AddIcon } from "../../../../../assets/icons";
import { SelectData } from "../../../../../models";
import { Button } from "../../../../forms/Button";
import { Toggle } from "../../../../forms/Input/Toggle";
import { MultiSelect } from "../../../../forms/MultiSelect";
import { TASKS_DASHBOARD_FILTERS } from "../../constants";
import {
  TasksDashboardFilterKey,
  TasksDashboardFilters,
  TasksDashboardVariant,
  TasksDashboardView,
} from "../../models";
import { TaskViewSwitcher } from "../TaskViewSwitcher";

interface TasksDashboardHeaderProps {
  variant: TasksDashboardVariant;
  title: string;
  view: TasksDashboardView;
  hideCompleted: boolean;
  hideUnassigned: boolean;
  /** False hides the "Hide unassigned" toggle — an assignee selection has already hidden that lane. */
  canHideUnassigned?: boolean;
  filters: TasksDashboardFilters;
  filterOptions: Record<TasksDashboardFilterKey, SelectData>;
  onViewChange: (view: TasksDashboardView) => void;
  onHideCompletedChange: (hide: boolean) => void;
  onHideUnassignedChange: (hide: boolean) => void;
  onFilterChange: (key: TasksDashboardFilterKey, values: string[]) => void;
  onNewTask?: () => void;
  /** Disable every control (toggles, filters, view tabs, New task) while the board is loading. */
  isLoading?: boolean;
}

export const TasksDashboardHeader = ({
  variant,
  title,
  view,
  hideCompleted,
  hideUnassigned,
  canHideUnassigned = true,
  filters,
  filterOptions,
  onViewChange,
  onHideCompletedChange,
  onHideUnassignedChange,
  onFilterChange,
  onNewTask,
  isLoading = false,
}: TasksDashboardHeaderProps): JSX.Element => (
  <div className={`TasksDashboardHeader TasksDashboardHeader_${variant}`}>
    <div className="TasksDashboardHeader_left">
      <h1>{title}</h1>
      <TaskViewSwitcher variant={variant} current={view} disabled={isLoading} onChange={onViewChange} />
    </div>

    <div className="TasksDashboardHeader_controls">
      <div className="TasksDashboardHeader_toggles">
        {/* Dropped entirely while an assignee is selected: that selection hides the Unassigned lane by
            itself, so the toggle could only ever be a control with nothing to switch. */}
        {canHideUnassigned && (
          <Toggle
            label="Hide unassigned"
            labelAlignment="left"
            value={hideUnassigned}
            disabled={isLoading}
            onChange={onHideUnassignedChange}
          />
        )}
        <Toggle
          label="Hide completed"
          labelAlignment="left"
          value={hideCompleted}
          disabled={isLoading}
          onChange={onHideCompletedChange}
        />
      </div>

      {/* Every filter takes several values at once (OR within a filter, AND across them). There is no
          "All" option to select: the dropdown's "Clear all" row empties that filter instead. */}
      <div className="TasksDashboardHeader_filters">
        {TASKS_DASHBOARD_FILTERS.map(({ key, placeholder }) => (
          <MultiSelect
            key={key}
            className="TasksDashboardHeader_filter"
            dropdownClassName={`TasksDashboardFilterDropdown TasksDashboardFilterDropdown_${variant}`}
            placeholder={placeholder}
            data={filterOptions[key]}
            values={filters[key] ?? []}
            corners={variant === "hub" ? "default" : "rounded"}
            showClearAll
            // The assignee and object lists run to the organisation's whole roster, so they are
            // searchable — as they were before these filters took several values at a time.
            showSearch
            disabled={isLoading}
            onChange={(values) => onFilterChange(key, values)}
          />
        ))}
      </div>

      <div className="TasksDashboardHeader_newTask">
        <Button
          variant="primary"
          size="small"
          minWidth={false}
          icon={<AddIcon aria-hidden />}
          iconPosition="left"
          text="New task"
          disabled={isLoading}
          onClick={() => onNewTask?.()}
        />
      </div>
    </div>
  </div>
);
